import { Autocomplete } from "@mui/material";
import { FormikValues, getIn } from "formik";

import { filterDropdownOptions } from "../../utils/filterDropdownOptions";
import { getFieldPath } from "../FormikField.utils";
import { FormikAutocompleteSelectProps, IOptionItem } from "./FormikAutocomplete.types";
import { FormikAutocompleteTextField } from "./components/FormikAutocompleteTextField";

/**
 * A dropdown restricted to its configured entries. Options marked {@link IOptionItem#isHidden} still
 * resolve a stored value to its label but are left out of the list, so a deactivated entry cannot be
 * picked again. A stored value with no option at all falls back to showing itself.
 */
export function FormikAutocompleteSelect<T extends FormikValues>(props: FormikAutocompleteSelectProps<T>) {
  const {
    fieldName,
    path,
    textFieldProps,
    formik,
    helperText,
    label,
    options,
    required,
    optional,
    onValueChange,
    ariaLabelledBy,
    ...others
  } = props;

  const fullPath = getFieldPath(path, fieldName);
  const id = (getIn(formik.values, fullPath) as string | null | undefined) ?? null;
  const matchedOption = id ? options.find((option) => option.id === id) : null;
  const unknownOption: IOptionItem | null = id && !matchedOption ? { id, label: id } : null;
  const value = matchedOption ?? unknownOption;
  const effectiveOptions = unknownOption ? [...options, unknownOption] : options;

  return (
    <Autocomplete<IOptionItem, false, boolean>
      value={value}
      onChange={(_, newValue, reason) => {
        const nextId = reason === "clear" ? null : (newValue?.id ?? null);
        void formik.setFieldValue(fullPath, nextId);
        onValueChange?.(nextId);
      }}
      isOptionEqualToValue={(option, val) => option.id === val.id}
      getOptionLabel={(option) => option.label}
      options={effectiveOptions}
      filterOptions={(opts, state) =>
        filterDropdownOptions(opts, state).filter((o) => !o.isHidden && o.id !== unknownOption?.id)
      }
      renderInput={(params) => (
        <FormikAutocompleteTextField
          params={params}
          textFieldProps={textFieldProps}
          formik={formik}
          fieldPath={fullPath}
          helperText={helperText}
          label={label}
          required={required}
          optional={optional}
          ariaLabelledBy={ariaLabelledBy}
        />
      )}
      fullWidth
      {...others}
    />
  );
}
